// ── Colors ──────────────────────────────────────────────
export const COLORS = {
  void: 0x08080d,
  healthy: 0x4ecdc4,
  driftLow: 0x4ecdc4,       // teal
  driftMid: 0xfb923c,       // orange
  driftHigh: 0xff6b6b,      // red
  taskQueue: 0xa78bfa,       // purple
  success: 0xffe66d,         // gold
  compute: 0x60a5fa,         // electric blue
  platform: 0x1a1a2e,
  platformGlow: 0x4ecdc4,
  nebula1: 0x1a0533,
  nebula2: 0x0a1628,
} as const;

// ── Platform ────────────────────────────────────────────
export const PLATFORM = {
  radius: 8,
  height: 0.3,
  segments: 64,
  agentArcRadius: 5.5,      // distance from center for agent slots
  maxAgents: 10,
} as const;

// ── Difficulty Curves (keyed by time in seconds) ────────
// Values are interpolated linearly between keyframes
// Balanced so 1 agent can comfortably handle the first 60s
export const DIFFICULTY_CURVE = {
  taskSpawnInterval: { 0: 7, 60: 5, 120: 4, 300: 2.5, 480: 1.5, 720: 1 },
  agentDriftSpeed:   { 0: 0.03, 60: 0.05, 120: 0.07, 300: 0.10, 480: 0.15, 720: 0.25 },
  errorSubtlety:     { 0: 0, 120: 0.15, 300: 0.4, 480: 0.7, 720: 1 },
  taskComplexity:    { 0: 4, 120: 4, 300: 5, 480: 6, 720: 8 },
  queueMaxSize:      { 0: 10, 120: 8, 300: 6, 480: 5, 720: 4 },
  taskTimeout:       { 0: 20, 60: 18, 120: 15, 300: 10, 480: 8, 720: 6 },
  priorityChance:    { 0: 0, 120: 0.05, 300: 0.15, 480: 0.3, 720: 0.5 },
  correctionCooldown:{ 0: 2.5, 120: 3, 300: 4, 480: 5, 720: 6 },
  agentSpawnCost:    { 0: 100, 120: 100, 300: 150, 480: 225, 720: 340 },
} as const;

// ── Scoring ─────────────────────────────────────────────
export const SCORING = {
  apmWeights: { dr: 0.20, vs: 0.25, ce: 0.25, ta: 0.30 },
  baseTaskReward: 25,
  baseScoreReward: 100,       // score points per correct validation
  priorityMultiplier: 3,
  comboMultiplier: 0.15,      // extra % per combo level (score + compute)
  wrongApprovalPenalty: 80,   // harsh — you let bad code through
  wrongRejectionPenalty: 30,
  correctRejectionBonus: 40,
  optimalDriftMin: 0.30,
  optimalDriftMax: 0.75,
  queueOverflowPenalty: 50,   // score lost per overflow
} as const;

// ── Compute Economy ─────────────────────────────────────
export const ECONOMY = {
  startingCompute: 0,
  spawnCostBase: 100,
  spawnCostMultiplier: 1.5,  // each successive agent costs more
  despawnRefundRate: 0.5,
  upgradeSpeedCost: 75,
  emergencyRepairCost: 200,
} as const;

// ── Game Rules ──────────────────────────────────────────
export const RULES = {
  maxLives: 3,
  correctionDriftReduction: 0.35,  // how much drift is reduced on correction
  lateCorrection85Reduction: 0.15, // reduced effect when correcting above 85%
} as const;

// ── Visual ──────────────────────────────────────────────
export const VISUAL = {
  bloomStrength: 0.7,
  bloomRadius: 0.3,
  bloomThreshold: 0.35,
  particleCount: 150,
  cameraDistance: 14,
  cameraHeight: 14,       // higher = more top-down, clearer layout
  autoOrbitSpeed: 0.05,   // slower orbit, less disorienting
} as const;
